import { Dropdown, Option, type OptionOnSelectData, type SelectionEvents } from '@fluentui/react-components';
import type { Column } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { resetFilter } from '../../_shared/reset.js';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetSelectColumnFilterValue } from '../filter.js';

type SelectFilterProps<TData, TValue> = {
    column: Column<TData, TValue>;
    options: {
        value: string | number | boolean | null;
        label: string;
    }[];
};

export function SelectFilter<TData, TValue>({ column, options }: SelectFilterProps<TData, TValue>) {
    const sharedStyles = useSharedStyles();

    const currentFilterValue = column.getFilterValue();

    const currentOption = useMemo(() => {
        return (
            options.find((option) => option.value === currentFilterValue) ?? {
                value: '',
                label: 'Alle',
            }
        );
    }, [currentFilterValue, options]);

    const [dropdownValue, setDropdownValue] = useState(JSON.stringify(currentOption.value));

    const onDropdownChange = useCallback(
        (_event: SelectionEvents, changeData: OptionOnSelectData) => {
            const value = changeData.selectedOptions[0];
            const parsedValue: string | undefined = value ? JSON.parse(value) : undefined;

            setDropdownValue(value);

            if (parsedValue) {
                typedSetSelectColumnFilterValue(column, parsedValue);
            } else {
                resetFilter(column);
            }
        },
        [column]
    );

    return (
        <Dropdown
            className={sharedStyles.dropdown}
            value={currentOption.label}
            selectedOptions={dropdownValue ? [dropdownValue] : []}
            onOptionSelect={onDropdownChange}
            listbox={{
                className: sharedStyles.dropdownListbox,
            }}
        >
            <Option value="" text="Alle">
                Alle
            </Option>

            {options.map((option) => {
                return (
                    <Option value={JSON.stringify(option.value)} key={JSON.stringify(option.value)} text={option.label}>
                        {option.label}
                    </Option>
                );
            })}
        </Dropdown>
    );
}
